import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { formatISO } from "date-fns";
import { diffArrays } from "diff";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useToast } from "../components/Toast";
import SpinningCircle from "../components/SpinningCircle";

interface ProjectDetailsReviewChangesProps {
  projectId: number;
  output: string;
  stagedOutput: string;
  onSaveChanges: (finalOutput: string) => void;
}

type ProcessedDiff =
  | {
      type: "change";
      oldValue: string[];
      newValue: string[];
      index: number;
    }
  | {
      type: "add" | "remove" | "unchanged";
      value: string[];
      index: number;
    };

const processBlocksWithHighlighting = (
  blocks: string[],
  bgColor: string,
  textColor: string,
  decision: "accept" | "reject" | undefined,
  blockType: "add" | "remove"
) => {
  const shouldHighlight = decision === undefined;
  const grayTextColor = "text-gray-400";
  let styleClass = "";
  if (
    (decision === "accept" && blockType === "add") ||
    (decision === "reject" && blockType === "remove")
  ) {
    styleClass = "font-bold";
  } else if (
    (decision === "accept" && blockType === "remove") ||
    (decision === "reject" && blockType === "add")
  ) {
    styleClass = `${grayTextColor} line-through`;
  } else if (shouldHighlight) {
    styleClass = `${bgColor} ${textColor}`;
  }
  return blocks
    .map((block) => {
      // For headings, we need special handling
      if (block.match(/^#{1,6}\s+/)) {
        return block.replace(
          /^(#{1,6})\s+(.+)$/gm,
          (match, hashes, text) =>
            `${hashes} <span class="${styleClass}">${text}</span>`
        );
      }

      // For nested lists
      if (
        block.includes("\n") &&
        (block.match(/^[-*+]\s+/) || block.match(/^\d+\.\s+/))
      ) {
        // Process each line of the list separately
        return block
          .split("\n")
          .map((listItem) => {
            // Match any amount of whitespace followed by list marker
            return listItem.replace(
              /^(\s*)([-*+]|\d+\.)\s+(.+)$/,
              (match, indent, bullet, text) =>
                `${indent}${bullet} <span class="${styleClass}">${text}</span>`
            );
          })
          .join("\n");
      }

      // For single-line list items, preserve the bullet/number
      if (block.match(/^[-*+]\s+/) || block.match(/^\d+\.\s+/)) {
        return block.replace(
          /^([-*+]|\d+\.)\s+(.+)$/gm,
          (match, bullet, text) =>
            `${bullet} <span class="${styleClass}">${text}</span>`
        );
      }

      // For blockquotes
      if (block.match(/^>\s+/)) {
        return block.replace(
          /^(>+\s+)(.+)$/gm,
          (match, quote, text) =>
            `${quote}<span class="${styleClass}">${text}</span>`
        );
      }

      // Default case - wrap the entire block
      return shouldHighlight
        ? `<span class="${styleClass}">${block}</span>`
        : block;
    })
    .join("\n\n");
};

const bgColorRemovalBlock = "bg-red-100";
const bgColorAdditionBlock = "bg-green-100";
const textColorRemovalBlock = "text-red-800";
const textColorAdditionBlock = "text-green-800";

const generateControlsHtml = (
  index: number,
  decision: "accept" | "reject" | undefined
) => {
  return `<div class="flex justify-between items-center py-2">
    <div class="flex space-x-2">
      <button 
        data-diff-index="${index}"
        data-diff-decision="reject"
        class="diff-decision-btn inline-flex items-center px-2 py-1 text-xs rounded-md ${
          decision === "reject"
            ? `${bgColorRemovalBlock} ${textColorRemovalBlock}`
            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
        }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
        Reject
      </button>
      <button 
        data-diff-index="${index}"
        data-diff-decision="accept"
        class="diff-decision-btn inline-flex items-center px-2 py-1 text-xs rounded-md ${
          decision === "accept"
            ? `${bgColorAdditionBlock} ${textColorAdditionBlock}`
            : "bg-white text-green-700 border border-green-300 hover:bg-green-50"
        }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        Accept
      </button>
    </div>
  </div>`;
};

// Parse markdown into logical blocks for diffing algorithm
const parseMarkdownBlocks = (markdown: string) => {
  const blocks = [];
  let currentBlock = "";

  // Split by lines first
  const lines = markdown.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = lines[i + 1] || "";

    // Check if this line starts a new block
    const isEmptyLine = line.trim() === "";
    // check for headers (##), lists (-, *, 1., 1), blockquotes (>, > >, |), or empty lines
    const nextIsNewBlock =
      nextLine.match(/^(#{1,6}|[-*+]|\d+\.|>)\s+/) || nextLine.trim() === "";

    // Add the current line to the current block
    currentBlock += line;

    // If this is the end of a block, push it and reset
    if (isEmptyLine || nextIsNewBlock || i === lines.length - 1) {
      if (currentBlock.trim()) {
        blocks.push(currentBlock.trim());
      }
      currentBlock = "";
    } else {
      // Add a newline if we're continuing the same block
      currentBlock += "\n";
    }
  }

  return blocks;
};

const ProjectDetailsReviewChanges = ({
  projectId,
  output,
  stagedOutput,
  onSaveChanges,
}: ProjectDetailsReviewChangesProps) => {
  const { getAccessTokenSilently } = useAuth0();
  const [diffDecisions, setDiffDecisions] = useState<
    Record<number, "accept" | "reject" | undefined>
  >({});
  const { showToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const oldBlocks = useMemo(() => parseMarkdownBlocks(output), [output]);
  const newBlocks = useMemo(
    () => parseMarkdownBlocks(stagedOutput),
    [stagedOutput]
  );

  const blockDiff = useMemo(
    () => diffArrays(oldBlocks, newBlocks),
    [oldBlocks, newBlocks]
  );

  // Compile the final output based on user decisions
  const compileFinalOutput = useCallback(
    (decisions: Record<number, "accept" | "reject" | undefined>) => {
      let result: string[] = [];

      for (let i = 0; i < blockDiff.length; i++) {
        const part = blockDiff[i];
        const decision = decisions[i];

        // Check if this is part of a change (removal followed by addition)
        const isPartOfChange =
          part.removed && i < blockDiff.length - 1 && blockDiff[i + 1].added;

        if (isPartOfChange) {
          // This is the removal part of a change
          const nextPart = blockDiff[i + 1];

          if (decision === "accept") {
            // If accepted, include the new version (addition)
            result = result.concat(nextPart.value);
          } else if (decision === "reject" || decision === undefined) {
            // If rejected or pending, include the old version (removal)
            result = result.concat(part.value);
          } else {
            throw new Error("Invalid decision");
          }

          // Skip the next part since we've handled it
          i++;
        } else if (part.added) {
          // For added blocks, include them if accepted
          if (decision === "accept") {
            result = result.concat(part.value);
          } else if (decision === undefined) {
            // TODO: how do we handle no decision? should we reject the change or include both?
            throw new Error("Invalid decision");
          }
          // if rejected, do nothing
        } else if (part.removed) {
          // For removed blocks, include them if rejected
          if (decision === "reject") {
            result = result.concat(part.value);
          } else if (decision === undefined) {
            // TODO: how do we handle no decision? should we reject the change or include both?
            throw new Error("Invalid decision");
          }
          // if accepted, do nothing
        } else {
          // Always include unchanged blocks
          result = result.concat(part.value);
        }
      }

      // Process the result to properly handle items that need single newlines instead of double newlines (i.e. part of the same markdown block)
      let finalOutput = "";
      for (let i = 0; i < result.length; i++) {
        const block = result[i];
        const nextBlock = i < result.length - 1 ? result[i + 1] : null;

        // Add the current block
        finalOutput += block;

        if (!nextBlock) continue; // No more blocks

        // Determine what separator to use
        const needsSingleNewline =
          // Lists
          (block.match(/^(\s*)([-*+]|\d+\.)\s+/) &&
            nextBlock.match(/^(\s*)([-*+]|\d+\.)\s+/)) ||
          // Table rows
          (block.match(/^\|(.+)\|$/) && nextBlock.match(/^\|(.+)\|$/)) ||
          // Continued blockquotes
          (block.match(/^>\s+/) && nextBlock.match(/^>\s+/));

        if (needsSingleNewline) {
          finalOutput += "\n";
        } else {
          finalOutput += "\n\n";
        }
      }

      return finalOutput;
    },
    [blockDiff]
  );

  // Handle saving all changes
  const handleSaveChanges = useCallback(
    async (decisions: Record<number, "accept" | "reject" | undefined>) => {
      if (!projectId) return;

      const compiledOutput = compileFinalOutput(decisions);

      try {
        setIsSaving(true);
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}/output`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              output: compiledOutput,
              currentTimestamp: formatISO(new Date()),
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to save changes");
        }

        // Reset decisions
        setDiffDecisions({});
        onSaveChanges(compiledOutput);

        showToast("Changes saved successfully", "green");
      } catch (error) {
        console.error("Error saving changes:", error);
        showToast("Failed to save changes", "red");
      } finally {
        setIsSaving(false);
      }
    },
    [
      compileFinalOutput,
      getAccessTokenSilently,
      projectId,
      onSaveChanges,
      setDiffDecisions,
      showToast,
    ]
  );

  // Handle accepting all changes
  const handleAcceptAll = () => {
    const newDecisions: Record<number, "accept" | "reject" | undefined> = {};

    blockDiff.forEach((part, index) => {
      if (part.added || part.removed) {
        newDecisions[index] = "accept";
      }
    });

    handleSaveChanges(newDecisions);
  };

  // Handle rejecting all changes
  const handleRejectAll = () => {
    const newDecisions: Record<number, "accept" | "reject" | undefined> = {};

    blockDiff.forEach((part, index) => {
      if (part.added || part.removed) {
        newDecisions[index] = "reject";
      }
    });

    handleSaveChanges(newDecisions);
  };

  // Preprocess the diff to identify changes (consecutive remove+add pairs)
  const processedDiff: ProcessedDiff[] = useMemo(() => {
    const processed: ProcessedDiff[] = [];
    for (let i = 0; i < blockDiff.length; i++) {
      const current = blockDiff[i];
      const next = i < blockDiff.length - 1 ? blockDiff[i + 1] : null;

      // Check if this is a removal followed by an addition (i.e. a change)
      if (current.removed && next && next.added) {
        // This is a change - combine them
        processed.push({
          type: "change",
          oldValue: current.value,
          newValue: next.value,
          index: i,
        });

        // Skip the next item since we've processed it
        i++;
      } else {
        // This is a pure addition, removal, or unchanged block
        processed.push({
          type: current.added
            ? "add"
            : current.removed
            ? "remove"
            : "unchanged",
          value: current.value,
          index: i,
        });
      }
    }
    return processed;
  }, [blockDiff]);

  const getHaveAllDecisionsBeenMade = useCallback(
    (decisions: Record<number, "accept" | "reject" | undefined>) => {
      // Count how many blocks need decisions
      const numBlocksNeedingDecisions = processedDiff.filter(
        (item) =>
          item.type === "add" ||
          item.type === "remove" ||
          item.type === "change"
      ).length;

      // Count how many decisions have been made
      const numDecisionsMade = Object.keys(decisions).length;

      return numDecisionsMade >= numBlocksNeedingDecisions;
    },
    [processedDiff]
  );

  // Handle individual diff decisions
  const handleDiffDecision = useCallback(
    (index: number, decision: "accept" | "reject") => {
      setDiffDecisions((prev) => {
        const newDecisions = {
          ...prev,
          [index]: decision,
        };

        const haveAllDecisionsBeenMade =
          getHaveAllDecisionsBeenMade(newDecisions);
        if (haveAllDecisionsBeenMade) {
          handleSaveChanges(newDecisions);
        }
        return newDecisions;
      });
    },
    [setDiffDecisions, getHaveAllDecisionsBeenMade, handleSaveChanges]
  );

  // Process the diff to create markdown with highlighted blocks
  const processedContent = processedDiff
    .map((item) => {
      if (item.type === "unchanged") {
        // Unchanged blocks - just join them with double newlines
        return item.value.join("\n\n");
      }

      // at this point we know the block is added or removed or changed
      const index = item.index;
      const decision = diffDecisions[index];

      if (item.type === "change") {
        // This is a change (removal + addition)
        const { oldValue, newValue } = item;
        // Create control buttons HTML for changes
        const controlsHtml = generateControlsHtml(index, decision);

        // Process old value (removal) with red highlighting if not accepted
        const shouldHighlightOldValue = decision !== "accept";
        const bgColorOldValue = shouldHighlightOldValue
          ? bgColorRemovalBlock
          : "";
        const textColorOldValue = shouldHighlightOldValue
          ? textColorRemovalBlock
          : "";
        const processedOldValue = processBlocksWithHighlighting(
          oldValue,
          bgColorOldValue,
          textColorOldValue,
          decision,
          "remove"
        );

        // Process new value (addition) with green highlighting if accepted
        const shouldHighlightNewValue = decision !== "reject";
        const bgColorNewValue = shouldHighlightNewValue
          ? bgColorAdditionBlock
          : "";
        const textColorNewValue = shouldHighlightNewValue
          ? textColorAdditionBlock
          : "";
        const processedNewValue = processBlocksWithHighlighting(
          newValue,
          bgColorNewValue,
          textColorNewValue,
          decision,
          "add"
        );

        // Combine everything
        return `${controlsHtml}\n\n${processedOldValue}\n\n${processedNewValue}`;
      } else if (item.type === "add") {
        // Create control buttons HTML
        const controlsHtml = generateControlsHtml(index, decision);

        // Process blocks with green highlighting if not rejected
        const shouldHighlightAddition = decision !== "reject";
        const bgColor = shouldHighlightAddition ? bgColorAdditionBlock : "";
        const textColor = shouldHighlightAddition ? textColorAdditionBlock : "";

        const processedBlocks = processBlocksWithHighlighting(
          item.value,
          bgColor,
          textColor,
          decision,
          "add"
        );

        return `${controlsHtml}\n\n${processedBlocks}`;
      } else if (item.type === "remove") {
        // Create control buttons HTML
        const controlsHtml = generateControlsHtml(index, decision);

        // Process blocks with red highlighting if not accepted
        const shouldHighlightRemoval = decision !== "accept";
        const bgColor = shouldHighlightRemoval ? bgColorRemovalBlock : "";
        const textColor = shouldHighlightRemoval ? textColorRemovalBlock : "";

        const processedBlocks = processBlocksWithHighlighting(
          item.value,
          bgColor,
          textColor,
          decision,
          "remove"
        );

        return `${controlsHtml}\n\n${processedBlocks}`;
      }
      return item.value.join("\n\n");
    })
    .join("\n\n"); // Join with double newlines to preserve markdown structure

  // Set up event listener for the custom events from the buttons
  useEffect(() => {
    const handleDiffDecisionClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest(".diff-decision-btn");

      if (button) {
        const index = parseInt(
          button.getAttribute("data-diff-index") || "0",
          10
        );
        const decision = button.getAttribute("data-diff-decision") as
          | "accept"
          | "reject";

        handleDiffDecision(index, decision);
      }
    };

    // Add event listener to the document to catch all button clicks
    document.addEventListener("click", handleDiffDecisionClick);

    // Clean up
    return () => {
      document.removeEventListener("click", handleDiffDecisionClick);
    };
  }, [handleDiffDecision]);

  return (
    <div className="space-y-4">
      {/* Badge */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-700 ">
          Review Echo's latest updates
        </span>
        {isSaving ? (
          <SpinningCircle size="sm" />
        ) : (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleRejectAll}
              className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 whitespace-nowrap"
            >
              <XMarkIcon className="mr-2 h-4 w-4" />
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="inline-flex items-center px-3 py-2 border border-green-300 text-sm leading-4 font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
            >
              <CheckIcon className="mr-2 h-4 w-4" />
              Accept All
            </button>
          </div>
        )}
      </div>

      {/* Diff view */}
      <div className="prose prose-sm max-w-none prose-ul:mt-2 prose-li:mt-0.5">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
          {processedContent}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default ProjectDetailsReviewChanges;
